import type {
  CompanyApplicationListResult,
  FetchCompanyApplicationsParams,
  TalentSubmissionDetail,
} from "./applications-contract";
import { api } from "./api";

/**
 * Company-wide Applications inbox API (Employer Phase 1, PRD §9 / WP4).
 *
 * Talks to the PINNED aggregate contract in `applications-contract.ts`. The
 * backend endpoints land after this frontend: until they exist the server
 * returns 404, which is surfaced as an `ApplicationsApiError` with the HTTP
 * code so the hooks can show a normal error state without retry-hammering a
 * route that is not there yet.
 */

export class ApplicationsApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
  ) {
    super(message);
    this.name = "ApplicationsApiError";
  }
}

export function isApplicationsNotFoundError(error: unknown): boolean {
  return (
    error instanceof ApplicationsApiError &&
    (error.code === "HTTP_404" || error.code === "NOT_FOUND")
  );
}

function throwEnvelopeError(
  error: { code?: string; message?: string } | null | undefined,
  fallback: string,
): never {
  throw new ApplicationsApiError(
    error?.message || fallback,
    error?.code || "UNKNOWN",
  );
}

export async function fetchCompanyApplications(
  input: FetchCompanyApplicationsParams = {},
): Promise<CompanyApplicationListResult> {
  const params = new URLSearchParams();

  if (input.query?.trim()) {
    params.set("query", input.query.trim());
  }
  if (input.destinations?.length) {
    params.set("destinations", input.destinations.join(","));
  }
  if (input.statuses?.length) {
    params.set("statuses", input.statuses.join(","));
  }
  if (input.sort) {
    params.set("sort", input.sort);
  }
  if (input.page) {
    params.set("page", String(input.page));
  }
  if (input.pageSize) {
    params.set("pageSize", String(input.pageSize));
  }

  const res = await api.get<CompanyApplicationListResult>(
    `/api/jobs/company/applications${params.size ? `?${params.toString()}` : ""}`,
  );

  if (res.error) {
    throwEnvelopeError(res.error, "Could not load applications");
  }

  if (!res.data) {
    throw new ApplicationsApiError(
      "Applications list returned no data",
      "EMPTY_RESPONSE",
    );
  }

  return res.data;
}

export async function fetchTalentSubmissionDetail(
  submissionId: string,
): Promise<TalentSubmissionDetail> {
  const res = await api.get<TalentSubmissionDetail>(
    `/api/jobs/company/talent-submissions/${submissionId}`,
  );

  if (res.error) {
    throwEnvelopeError(res.error, "Could not load talent submission");
  }

  if (!res.data) {
    throw new ApplicationsApiError(
      "Talent submission returned no data",
      "EMPTY_RESPONSE",
    );
  }

  return res.data;
}
